import { useCallback, useState } from "react";

import type { DragEndEvent, DragOverEvent, DragStartEvent } from "@dnd-kit/core";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";

import { EMPTY_ARRAY } from "app/pages/sales-intelligence/constants/common";
import type { ScoringConfigRule } from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/modules/lists/types/columns";

import { resolveHoveredScoringColumn, scoringBoardColumnCollisionDetection } from "./scoringBoardDndUtils";
import { ScoringBoardDragOverlay, type ActiveDragState } from "./ScoringBoardDragOverlay";
import type { ScoringBoardAddRuleControlProps, ScoringBoardSharedProps } from "./scoringBoardTypes";
import { ScoringPriorityColumn } from "./ScoringPriorityColumn";
import { StyledScoringBoard } from "./styles";

const POINTER_ACTIVATION_CONSTRAINT = { distance: 5 };

type Props = ScoringBoardSharedProps &
    ScoringBoardAddRuleControlProps & {
        columns: readonly LeadPriorityScore[];
        rules: ScoringConfigRule;
        onAddRule: (priority: LeadPriorityScore) => void;
        onEditRule: (priority: LeadPriorityScore, ruleIndex: number) => void;
        onDeleteRule: (priority: LeadPriorityScore, ruleIndex: number) => void;
    };

export const ScoringBoard = ({
    columns,
    rules,
    filtersList,
    signalById,
    onAddRule,
    onEditRule,
    onDeleteRule,
    onMoveRule,
    addRuleControl,
    getRuleScope,
    canEditRule,
    getRuleUsage,
    selectedRuleId,
    selectedRuleIds,
    pinSelectedFirst,
    showUsedBadgeForSelected,
    onSelect,
    getSelectionDisabledReason,
    automationPrefix = "scoring-board",
}: Props) => {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: POINTER_ACTIVATION_CONSTRAINT }));
    const [activeDrag, setActiveDrag] = useState<ActiveDragState | null>(null);
    const [hoveredDropPriority, setHoveredDropPriority] = useState<LeadPriorityScore | null>(null);

    const clearDragState = useCallback(() => {
        setActiveDrag(null);
        setHoveredDropPriority(null);
    }, []);

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const sourcePriority = event.active.data.current?.sourcePriority as LeadPriorityScore | undefined;
        const ruleIndex = event.active.data.current?.ruleIndex as number | undefined;

        if (sourcePriority === undefined || ruleIndex === undefined) {
            return;
        }

        setActiveDrag({
            ruleId: String(event.active.id),
            width: event.active.rect.current.initial?.width ?? 0,
            sourcePriority,
            ruleIndex,
        });
        setHoveredDropPriority(null);
    }, []);

    const handleDragOver = useCallback((event: DragOverEvent) => {
        const sourcePriority = event.active.data.current?.sourcePriority as LeadPriorityScore | undefined;
        setHoveredDropPriority(resolveHoveredScoringColumn(event.over?.id, sourcePriority));
    }, []);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            if (!onMoveRule) {
                clearDragState();
                return;
            }

            const { active, over } = event;

            if (!over) {
                clearDragState();
                return;
            }

            const sourcePriority = active.data.current?.sourcePriority as LeadPriorityScore | undefined;
            const ruleIndex = active.data.current?.ruleIndex as number | undefined;
            const targetPriority = resolveHoveredScoringColumn(over.id, sourcePriority);

            clearDragState();

            if (sourcePriority === undefined || ruleIndex === undefined || targetPriority === null) {
                return;
            }

            onMoveRule(sourcePriority, ruleIndex, targetPriority);
        },
        [clearDragState, onMoveRule],
    );

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={scoringBoardColumnCollisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={clearDragState}
        >
            <StyledScoringBoard data-automation={automationPrefix}>
                {columns.map((priority) => (
                    <ScoringPriorityColumn
                        key={priority}
                        priority={priority}
                        rules={rules[priority] ?? EMPTY_ARRAY}
                        filtersList={filtersList}
                        signalById={signalById}
                        onAddRule={() => onAddRule(priority)}
                        onEditRule={(ruleIndex) => onEditRule(priority, ruleIndex)}
                        onDeleteRule={(ruleIndex) => onDeleteRule(priority, ruleIndex)}
                        onMoveRule={onMoveRule}
                        hoveredDropPriority={onMoveRule ? hoveredDropPriority : null}
                        addRuleControl={addRuleControl?.(priority)}
                        getRuleScope={getRuleScope}
                        canEditRule={canEditRule}
                        getRuleUsage={getRuleUsage}
                        selectedRuleId={selectedRuleId}
                        selectedRuleIds={selectedRuleIds}
                        pinSelectedFirst={pinSelectedFirst}
                        showUsedBadgeForSelected={showUsedBadgeForSelected}
                        onSelect={onSelect}
                        getSelectionDisabledReason={getSelectionDisabledReason}
                        automationPrefix={automationPrefix}
                    />
                ))}
            </StyledScoringBoard>
            {onMoveRule ? (
                <DragOverlay dropAnimation={null}>
                    <ScoringBoardDragOverlay
                        rules={rules}
                        activeDrag={activeDrag}
                        filtersList={filtersList}
                        signalById={signalById}
                        getRuleScope={getRuleScope}
                        getRuleUsage={getRuleUsage}
                    />
                </DragOverlay>
            ) : null}
        </DndContext>
    );
};
